import { useNavigate, useLocation } from 'react-router-dom'
import {
  SettingsOutlined as GeneralIcon,
  AppsOutlined as ServicesIcon,
  PublicOutlined as DomainsIcon,
  BugReportOutlined as ErrorsIcon,
  SubjectOutlined as TelemetryLogsIcon,
  TimelineOutlined as TracesIcon,
  StorageOutlined as QueriesIcon,
  ShowChartOutlined as MetricsIcon,
  AccountTreeOutlined,
} from '@mui/icons-material'
import { useOrganizationSettings } from '../../hooks/useOrganizationSettings'
import {
  TELEMETRY_NAV_VIEWS,
  parseTelemetryViewFromPathname,
  telemetryEnvironmentPath,
  type TelemetryNavView,
} from '../../utils/telemetryNav'
import {
  getEnvironmentSettingsNavItems,
  isEnvironmentSettingsNavItemActive,
  type EnvironmentSettingsPage,
} from '../../utils/environmentSettingsNav'
import { NavItem, SectionLabel } from './sidebarPrimitives'
import BetaTag from '../BetaTag'

export default function EnvironmentContextNav({
  projectId,
  envId,
  onNavigate,
}: {
  projectId: string
  envId: string
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { telemetryEnabled } = useOrganizationSettings()
  const showTelemetry = telemetryEnabled
  const activeTelemetryView = parseTelemetryViewFromPathname(location.pathname, projectId, envId)

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  return (
    <>
      <SectionLabel label="Setting Overrides" />
      {getEnvironmentSettingsNavItems(projectId, envId).map((item) => (
        <NavItem
          key={item.page}
          text={item.label}
          icon={settingsNavIcon(item.page)}
          active={isEnvironmentSettingsNavItemActive(location.pathname, projectId, envId, item.page)}
          onClick={() => go(item.path)}
          pl={1.5}
          fontSize="0.8125rem"
        />
      ))}

      {showTelemetry && (
        <>
          <SectionLabel label="Telemetry" tag={<BetaTag />} />
          {TELEMETRY_NAV_VIEWS.map((item) => (
            <NavItem
              key={item.id}
              text={item.label}
              icon={telemetryViewIcon(item.id)}
              active={activeTelemetryView === item.id}
              onClick={() => go(telemetryEnvironmentPath(projectId, envId, item.id))}
              pl={1.5}
              fontSize="0.8125rem"
            />
          ))}
        </>
      )}
    </>
  )
}

function settingsNavIcon(page: EnvironmentSettingsPage) {
  switch (page) {
    case 'general':
      return <GeneralIcon />
    case 'services':
      return <ServicesIcon />
    case 'custom-domains':
      return <DomainsIcon />
  }
}

function telemetryViewIcon(view: TelemetryNavView) {
  switch (view) {
    case 'errors':
      return <ErrorsIcon />
    case 'logs':
      return <TelemetryLogsIcon />
    case 'traces':
      return <TracesIcon />
    case 'queries':
      return <QueriesIcon />
    case 'metrics':
      return <MetricsIcon />
    case 'profiles':
      return <AccountTreeOutlined />
  }
}
